import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormField,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export function BillingForm() {
  const billingFormSchema = z.object({
    name: z.string().min(1, "Name is required"),
    cardNumber: z.string().min(1, "Card number is required"),
    expirationDate: z.string().min(1, "Expiration date is required"),
    cvv: z.string().min(1, "CVV is required"),
    state: z.string().min(1, "State is required"),
    city: z.string().min(1, "City is required"),
    address: z.string().min(1, "Address is required"),
    country: z.string().min(1, "Country is required"),
    postalCode: z.string().min(1, "Postal code is required"),
  });
  const form = useForm<z.infer<typeof billingFormSchema>>({
    resolver: zodResolver(billingFormSchema),
    defaultValues: {
      name: "",
      cardNumber: "",
      expirationDate: "",
      cvv: "",
      state: "",
      city: "",
      address: "",
      country: "",
      postalCode: "",
    },
  });

  return (
    <div>
      <Form {...form}>
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-sm font-bold text-neutral-800/80">
                  Name on card <span className="text-red-600">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    variant={"filled"}
                    className="h-[45px]"
                    placeholder="John Doe"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cardNumber"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-sm font-bold text-neutral-800/80">
                  Card Number <span className="text-red-600">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    variant={"filled"}
                    className="h-[45px]"
                    placeholder="1234 5678 9012 3456"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex w-full flex-row gap-3">
            <FormField
              control={form.control}
              name="expirationDate"
              render={({ field }) => (
                <FormItem className="w-1/2 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800/80">
                    Expiration <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant={"filled"}
                      className="h-[45px]"
                      placeholder="MM/YY"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cvv"
              render={({ field }) => (
                <FormItem className="w-1/2 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800/80">
                    CVV <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant={"filled"}
                      className="h-[45px]"
                      placeholder="000"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex w-full flex-row gap-3">
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem className="w-1/2 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800/80">
                    State <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant={"filled"}
                      className="h-[45px]"
                      placeholder="California"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="w-1/2 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800/80">
                    City <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant={"filled"}
                      className="h-[45px]"
                      placeholder="San Francisco"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="text-sm font-bold text-neutral-800/80">
                  Address <span className="text-red-600">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    variant={"filled"}
                    className="h-[45px]"
                    placeholder="123 Main St"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex w-full flex-row gap-3">
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="w-1/2 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800/80">
                    Country <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant={"filled"}
                      className="h-[45px]"
                      placeholder="United States"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postalCode"
              render={({ field }) => (
                <FormItem className="w-1/2 space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800/80">
                    Postal Code <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      variant={"filled"}
                      className="h-[45px]"
                      placeholder="12345"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <Button className="text-muted h-[30px] bg-neutral-100 text-xs leading-normal font-bold hover:bg-neutral-100/80">
            Save Details
          </Button>
          <Separator className="my-2 mb-4" />
          <Button type="submit" className="h-[48px] font-bold">
            Checkout
          </Button>
        </div>
      </Form>
    </div>
  );
}
